import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    CreatePerformanceTeamReview,
    CreatePerformanceTeamReviewFailure,
    CreatePerformanceTeamReviewSuccess,
    DeletePerformanceTeamReview,
    DeletePerformanceTeamReviewFailure,
    DeletePerformanceTeamReviewSuccess,
    FetchPerformanceTeamOverveview,
    FetchPerformanceTeamOverveviewFailure,
    FetchPerformanceTeamOverveviewSuccess,
    FetchPerformanceTeamReview,
    FetchPerformanceTeamReviewFailure,
    FetchPerformanceTeamReviewSuccess,
    FetchAvailablePerformanceTeamTemplateTypes,
    FetchAvailablePerformanceTeamTemplateTypesFailure,
    FetchAvailablePerformanceTeamTemplateTypesSuccess,
    SetPerformanceTeamReview,
    UnsetPerformanceTeamReview,
    UpdatePerformanceTeamReview,
    UpdatePerformanceTeamReviewFailure,
    UpdatePerformanceTeamReviewSuccess,
    FetchPerformanceTeamTemplateTypes,
    FetchPerformanceTeamTemplateTypesSuccess,
    FetchPerformanceTeamTemplateTypesFailure,
    FetchPerformanceTeamPeriods,
    FetchPerformanceTeamPeriodsSuccess,
    FetchPerformanceTeamPeriodsFailure,
    UpdatePerformanceTeamParticipants,
    UpdatePerformanceTeamParticipantsSuccess,
    UpdatePerformanceTeamParticipantsFailure,
    FetchPerformanceTeamMemberOverveview,
    FetchPerformanceTeamMemberOverveviewSuccess,
    FetchPerformanceTeamMemberOverveviewFailure,
} from '../actions';
import { AddPerformanceTeamReviews } from '../actions/add-entities.action';
import { PerformanceTeamReviewApiService } from '../services/performance-team-review.api-service';
import { PerformanceTeamReview } from './performance-team-review.model';
import { PerformanceTeamReviewStateModel as PerformanceTeamReviewStateModel } from './performance-team-review.state-model';

@State<PerformanceTeamReviewStateModel>({
    name: 'performanceTeamReview',
    defaults: new PerformanceTeamReviewStateModel(),
})
@Injectable()
export class PerformanceTeamReviewState extends EntityState<PerformanceTeamReview> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: PerformanceTeamReviewStateModel, id: string = state.item): PerformanceTeamReview {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: PerformanceTeamReviewStateModel): PerformanceTeamReview[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(PerformanceTeamReviewState.sortProperties));

        return toReturn;
    }

    constructor(private _performanceTeamReviewApiService: PerformanceTeamReviewApiService) {
        super(_performanceTeamReviewApiService, 0);
    }

    @Action(AddPerformanceTeamReviews)
    addPendingEntities(ctx: StateContext<PerformanceTeamReviewStateModel>, action: AddPerformanceTeamReviews): void {
        this._addEntities(ctx, action);
    }

    @Action(SetPerformanceTeamReview)
    setEntity(ctx: StateContext<PerformanceTeamReviewStateModel>, action: SetPerformanceTeamReview): void {
        this._setEntity(ctx, action, AddPerformanceTeamReviews);
    }

    @Action(UnsetPerformanceTeamReview)
    unsetEntity(ctx: StateContext<PerformanceTeamReviewStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreatePerformanceTeamReview)
    createPerformanceTeamReview(ctx: StateContext<PerformanceTeamReviewStateModel>, { payload }: CreatePerformanceTeamReview): void {
        this._performanceTeamReviewApiService.createPerformanceTeamReview(payload).subscribe(
            (entity) => {
                this._addEntities(ctx, { payload: [entity] });
                ctx.dispatch(new CreatePerformanceTeamReviewSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new CreatePerformanceTeamReviewFailure({ response }));
            }
        );
    }

    @Action(UpdatePerformanceTeamParticipants)
    updatePerformanceTeamParticipants(ctx: StateContext<PerformanceTeamReviewStateModel>, { payload }: UpdatePerformanceTeamParticipants): void {
        this._performanceTeamReviewApiService.updatePerformanceTeamParticipants(payload.teamReviewId, payload.userIds).subscribe(
            (entity) => {
                ctx.dispatch(new UpdatePerformanceTeamParticipantsSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new UpdatePerformanceTeamParticipantsFailure({ response }));
            }
        );
    }

    @Action(UpdatePerformanceTeamReview)
    updatePerformanceTeamReview(ctx: StateContext<PerformanceTeamReviewStateModel>, action: UpdatePerformanceTeamReview): void {
        this._performanceTeamReviewApiService.updatePerformanceTeamReview(action.payload.performanceTeamReviewId, action.payload.performanceTeamReview).subscribe(
            (entity) => {
                this._addEntities(ctx, { payload: [entity] });
                ctx.dispatch(new UpdatePerformanceTeamReviewSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new UpdatePerformanceTeamReviewFailure());
            }
        );
    }

    @Action(FetchPerformanceTeamOverveview)
    fetchPerformanceTeamOverveview(ctx: StateContext<PerformanceTeamReviewStateModel>, { payload }: FetchPerformanceTeamOverveview): void {
        this._performanceTeamReviewApiService.getPerformanceTeamOverview(payload).subscribe(
            (entity) => {
                ctx.dispatch(new FetchPerformanceTeamOverveviewSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchPerformanceTeamOverveviewFailure());
            }
        );
    }

    @Action(FetchPerformanceTeamMemberOverveview)
    fetchPerformanceTeamMemberOverveview(ctx: StateContext<PerformanceTeamReviewStateModel>, action: FetchPerformanceTeamMemberOverveview): void {
        this._performanceTeamReviewApiService.getPerformanceTeamMemberOverview(action.payload.teamId, action.payload.userId).subscribe(
            (entity) => {
                ctx.dispatch(new FetchPerformanceTeamMemberOverveviewSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchPerformanceTeamMemberOverveviewFailure());
            }
        );
    }

    @Action(FetchPerformanceTeamPeriods)
    fetchPerformanceTeamPeriods(ctx: StateContext<PerformanceTeamReviewStateModel>, { payload }: FetchPerformanceTeamPeriods): void {
        this._performanceTeamReviewApiService.getPerformanceTeamPeriods(payload).subscribe(
            (entity) => {
                ctx.dispatch(new FetchPerformanceTeamPeriodsSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchPerformanceTeamPeriodsFailure());
            }
        );
    }

    @Action(FetchAvailablePerformanceTeamTemplateTypes)
    getAvailablePerformanceTemplateTypesTeams(ctx: StateContext<PerformanceTeamReviewStateModel>, action: FetchAvailablePerformanceTeamTemplateTypes): void {
        this._performanceTeamReviewApiService.getAvailablePerformanceTemplateTypesTeams(action.payload).subscribe(
            (entities) => {
                ctx.dispatch(new FetchAvailablePerformanceTeamTemplateTypesSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchAvailablePerformanceTeamTemplateTypesFailure());
            }
        );
    }

    @Action(FetchPerformanceTeamTemplateTypes)
    getPerformanceTemplateTypesTeams(ctx: StateContext<PerformanceTeamReviewStateModel>, action: FetchPerformanceTeamTemplateTypes): void {
        this._performanceTeamReviewApiService.getPerformanceTemplateTypesTeams(action.payload).subscribe(
            (entities) => {
                ctx.dispatch(new FetchPerformanceTeamTemplateTypesSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchPerformanceTeamTemplateTypesFailure());
            }
        );
    }

    @Action(DeletePerformanceTeamReview)
    deleteEntity(ctx: StateContext<PerformanceTeamReviewStateModel>, action: DeletePerformanceTeamReview): void {
        this._deleteEntity(ctx, action, DeletePerformanceTeamReviewSuccess, DeletePerformanceTeamReviewFailure).then(() => {});
    }

    @Action(FetchPerformanceTeamReview)
    fetchEntity(ctx: StateContext<PerformanceTeamReviewStateModel>, action: FetchPerformanceTeamReview): void {
        this._fetchEntity(ctx, action, FetchPerformanceTeamReviewSuccess, FetchPerformanceTeamReviewFailure, AddPerformanceTeamReviews, SetPerformanceTeamReview);
    }

    @Action(FetchPerformanceTeamReviewSuccess)
    fetchEntitySuccess(ctx: StateContext<PerformanceTeamReviewStateModel>, action: FetchPerformanceTeamReviewSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchPerformanceTeamReviewFailure)
    fetchEntityFailure(ctx: StateContext<PerformanceTeamReviewStateModel>, action: FetchPerformanceTeamReviewFailure): void {
        this._fetchEntityFailure(ctx, action);
    }
}
